import { Injectable } from '@angular/core';
import { jwtDecode } from 'jwt-decode';

import StorageService from './storage.service';

type JwtToken = {
  user: object | null;
  exp: number;
  fullname: string;
};

const defaultToken: JwtToken = { user: null, exp: 0, fullname: '' };

/**
 * Service for JWT token management and validation.
 * Handles token storage, decoding, and expiration checks.
 */
@Injectable({
  providedIn: 'root',
})
class JWTTokenService {
  jwtToken: string = '';
  token: JwtToken = defaultToken;

  constructor(private readonly _storage: StorageService) {}

  setToken(token: string | null): this {
    if (token) {
      this.jwtToken = token;
      this.token = jwtDecode(token);
      this._storage.setItem('token', this.jwtToken);
    } else {
      this._storage.deleteItem('token');
      this.clear();
      this.token = defaultToken;
      this.jwtToken = '';
    }

    return this;
  }

  setKeepConnection(keepConnection: boolean): this {
    this._storage.setItem('keepConnection', keepConnection.toString());

    return this;
  }

  setRefreshToken(token: string | null): this {
    if (token) {
      this._storage.setItem('refreshToken', token);
    } else {
      this._storage.deleteItem('refreshToken');
    }

    return this;
  }

  getToken(): string | null {
    if (this.jwtToken) return this.jwtToken;

    const token: string | null = this._storage.getItem('token') ?? null;

    if (token) {
      this.jwtToken = token;
      this.token = jwtDecode(token);

      return token;
    }

    return null;
  }

  getDecodeToken(): JwtToken {
    return jwtDecode(this.jwtToken);
  }

  getUser() {
    return this.token.user;
  }

  getExpiryTime(): number {
    return this.token.exp;
  }

  isTokenExpired(): boolean {
    const expiryTime: number = this.getExpiryTime();

    if (expiryTime === 0) return false;
    return Date.now() > expiryTime * 1000;
  }

  isConnected(): boolean {
    return !!this.getToken() && !this.isTokenExpired();
  }

  clear() {
    this._storage.deleteItem('token');
    this._storage.deleteItem('refreshToken');
  }
}

export default JWTTokenService;
